import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { notificationsAPI, authAPI } from '../../services/api';

/* ───── Notification type icons ───── */
const typeIcons = {
  registration: '👤',
  milestone: '🏆',
  payment: '💰',
  opportunity: '🌱',
  volunteering: '🤝',
  general: '📢',
};

/* ───── Time-ago helper ───── */
function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export default function DashboardLayout({ children, navItems, title }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const dropdownRef = useRef(null);

  /* ── Fetch unread count on route change ── */
  useEffect(() => {
    notificationsAPI.getAll({ unread_only: true, per_page: 1 })
      .then(res => setUnreadCount(res.data.unread_count))
      .catch(() => {});
  }, [location]);

  /* ── Fetch notifications when dropdown opens ── */
  const fetchNotifications = useCallback((p = 1) => {
    setLoading(true);
    notificationsAPI.getAll({ page: p, per_page: 15 })
      .then(res => {
        setNotifications(p === 1 ? res.data.items : prev => [...prev, ...res.data.items]);
        setTotalPages(res.data.pages);
        setPage(p);
        setUnreadCount(res.data.unread_count);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (showDropdown) fetchNotifications(1);
  }, [showDropdown, fetchNotifications]);

  /* ── Close dropdown on outside click ── */
  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showDropdown]);

  /* ── Close dropdown on route change ── */
  useEffect(() => { setShowDropdown(false); }, [location]);

  /* ── Mark single notification as read ── */
  const markRead = (id) => {
    notificationsAPI.markRead(id).then(() => {
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }).catch(() => {});
  };

  /* ── Mark all as read ── */
  const markAllRead = () => {
    notificationsAPI.markAllRead().then(() => {
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    }).catch(() => {});
  };

  /* ── Change Password modal ── */
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwdMsg, setPwdMsg] = useState({ type: '', text: '' });
  const [pwdLoading, setPwdLoading] = useState(false);

  const handlePwdChange = async (e) => {
    e.preventDefault();
    setPwdMsg({ type: '', text: '' });
    if (pwdForm.new_password.length < 6) {
      setPwdMsg({ type: 'error', text: 'New password must be at least 6 characters' });
      return;
    }
    if (pwdForm.new_password !== pwdForm.confirm_password) {
      setPwdMsg({ type: 'error', text: 'Passwords do not match' });
      return;
    }
    setPwdLoading(true);
    try {
      await authAPI.changePassword({
        current_password: pwdForm.current_password,
        new_password: pwdForm.new_password,
      });
      setPwdMsg({ type: 'success', text: 'Password changed successfully!' });
      setPwdForm({ current_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => setShowPwdModal(false), 1500);
    } catch (err) {
      setPwdMsg({ type: 'error', text: err.response?.data?.error || 'Failed to change password' });
    } finally {
      setPwdLoading(false);
    }
  };

  const baseUrl = `/${user?.role === 'admin' ? 'admin' : user?.role}`;

  return (
    <div className="app-layout">
      <aside className="sidebar">
        <div className="sidebar-logo" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <img src={`${process.env.PUBLIC_URL || ''}/haritanjali_logo.png`} alt="Haritanjali" style={{ width: 52, height: 52, objectFit: 'contain' }} />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#ffffff', lineHeight: 1.1 }}>हरितांजलि</div>
            <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.7)', letterSpacing: 2, textTransform: 'uppercase', fontWeight: 600, marginTop: 1 }}>HARITANJALI</div>
            <div style={{ fontSize: 9, color: '#F5A623', fontStyle: 'italic', marginTop: 2 }}>
              जहाँ पेड़, वहाँ जीवन
            </div>
          </div>
        </div>
        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <React.Fragment key={item.path || item.section}>
              {item.section ? (
                <div className="sidebar-section">{item.section}</div>
              ) : (
                <Link
                  to={`${baseUrl}${item.path}`}
                  className={location.pathname === `${baseUrl}${item.path}` ? 'active' : ''}
                >
                  {item.icon} {item.label}
                </Link>
              )}
            </React.Fragment>
          ))}
        </nav>
      </aside>

      <div className="main-content">
        <header className="top-header">
          <h2>{title}</h2>
          <div className="header-actions">
            {/* ── Notification bell with dropdown ── */}
            <div className="notif-wrapper" ref={dropdownRef}>
              <div
                className="notification-badge"
                title="Notifications"
                onClick={() => setShowDropdown(prev => !prev)}
              >
                <span role="img" aria-label="notifications">&#128276;</span>
                {unreadCount > 0 && <span className="count">{unreadCount}</span>}
              </div>

              {showDropdown && (
                <div className="notif-dropdown">
                  {/* Header */}
                  <div className="notif-dropdown-header">
                    <span style={{ fontWeight: 700, fontSize: 15 }}>Notifications</span>
                    {unreadCount > 0 && (
                      <button className="notif-mark-all" onClick={markAllRead}>
                        Mark all read
                      </button>
                    )}
                  </div>

                  {/* List */}
                  <div className="notif-dropdown-list">
                    {notifications.length === 0 && !loading && (
                      <div className="notif-empty">
                        <span role="img" aria-label="empty" style={{ fontSize: 28 }}>🔔</span>
                        <p>No notifications yet</p>
                      </div>
                    )}

                    {notifications.map(n => (
                      <div
                        key={n.id}
                        className={`notif-item${n.is_read ? '' : ' unread'}`}
                        onClick={() => !n.is_read && markRead(n.id)}
                      >
                        <div className="notif-item-icon">
                          {typeIcons[n.notification_type] || '📢'}
                        </div>
                        <div className="notif-item-body">
                          <div className="notif-item-title">{n.title}</div>
                          <div className="notif-item-msg">{n.message}</div>
                          <div className="notif-item-time">{timeAgo(n.created_at)}</div>
                        </div>
                        {!n.is_read && <div className="notif-unread-dot" />}
                      </div>
                    ))}

                    {loading && (
                      <div style={{ textAlign: 'center', padding: 16, color: '#999' }}>Loading…</div>
                    )}

                    {!loading && page < totalPages && (
                      <button
                        className="notif-load-more"
                        onClick={() => fetchNotifications(page + 1)}
                      >
                        Load more
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            <span style={{ fontSize: 14, color: '#666' }}>{user?.name}</span>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => { setPwdMsg({ type: '', text: '' }); setPwdForm({ current_password: '', new_password: '', confirm_password: '' }); setShowPwdModal(true); }}
              title="Change Password"
              style={{ padding: '4px 10px', fontSize: 13 }}
            >
              🔑 Change Password
            </button>
            <button className="btn btn-secondary btn-sm" onClick={logout}>Logout</button>
          </div>
        </header>
        <div className="page-content">
          {children}
        </div>
      </div>

      {/* ── Change Password Modal ── */}
      {showPwdModal && (
        <div className="modal-overlay" onClick={() => setShowPwdModal(false)}>
          <div className="modal-content" style={{ maxWidth: 400 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>Change Password</h3>
              <button className="modal-close" onClick={() => setShowPwdModal(false)}>&times;</button>
            </div>
            <form onSubmit={handlePwdChange} style={{ padding: '20px 24px' }}>
              {pwdMsg.text && (
                <div className={pwdMsg.type === 'error' ? 'error' : 'success'} style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, fontSize: 13 }}>
                  {pwdMsg.text}
                </div>
              )}
              <div className="form-group">
                <label>Current Password *</label>
                <input
                  type="password"
                  value={pwdForm.current_password}
                  onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })}
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>New Password *</label>
                <input
                  type="password"
                  value={pwdForm.new_password}
                  onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })}
                  required
                  minLength={6}
                />
              </div>
              <div className="form-group">
                <label>Confirm New Password *</label>
                <input
                  type="password"
                  value={pwdForm.confirm_password}
                  onChange={(e) => setPwdForm({ ...pwdForm, confirm_password: e.target.value })}
                  required
                  minLength={6}
                />
              </div>
              <button type="submit" className="btn btn-primary btn-block" disabled={pwdLoading} style={{ marginTop: 8 }}>
                {pwdLoading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
